import React from 'react';
import { Text, Tooltip } from '@perch-labs/shrike';
import { capitalize } from '@perch-labs/warbler';
import { copyFor } from 'config';
import {
  ChartBar,
  ChartWrapper,
  KeyWrapper,
  KeyItem,
  KeySquare,
} from './energy-breakdown-chart.style';

const getCopy = copyFor('solar.energyBreakdownChart');
const getTypesCopy = copyFor('solar.energyBreakdownChart.energyTypes');
const energyTypeToColor = {
  biomass: '#BBF3C0',
  coal: '#525C62',
  gas: '#6E7B84',
  geothermal: '#8CDA93',
  hydro: '#36A93F',
  nuclear: '#66ACFD',
  oil: '#8A969D',
  otherFossil: '#B2B9BD',
  solar: '#26762C',
  wind: '#1B511F',
};
type NonNullEgridSubregion = NonNullable<
  GetEgridSubregionQuery['zipCode']['egridSubregion']
>;
type NonNullEnergyBreakdown = NonNullEgridSubregion['energyBreakdown'];
type EnergyKey = keyof NonNullEnergyBreakdown;
type EnergyKeyWithTypename = EnergyKey | '__typename';
interface Props {
  energyBreakdown: NonNullEnergyBreakdown;
}

export const EnergyBreakdownChart = ({ energyBreakdown }: Props) => {
  const energyKeys = (
    Object.keys(energyBreakdown) as EnergyKeyWithTypename[]
  ).filter(
    // Need to filter this out because its an extra field that Apollo adds
    key => key !== '__typename'
  ) as EnergyKey[];
  const other =
    1 - energyKeys.reduce((acc, key) => acc + energyBreakdown[key], 0);
  const updatedEnergyBreakdown = { ...energyBreakdown };
  updatedEnergyBreakdown.otherFossil += other;

  return (
    <>
      <ChartWrapper>
        {energyKeys.map(key => {
          return (
            <Tooltip
              key={key}
              content={`${capitalize(key)}: ${Math.round(
                updatedEnergyBreakdown[key] * 100
              )}%`}
              tooltipId={`breakdown-${key}`}
              label={getCopy('label')}
              tooltippedElementWrapperWidth="100%"
              tooltipWrapperWidth={`${updatedEnergyBreakdown[key] * 100}%`}
            >
              <ChartBar
                data-testid="chart-bar"
                background={energyTypeToColor[key]}
              />
            </Tooltip>
          );
        })}
      </ChartWrapper>
      <KeyWrapper aria-hidden={true}>
        {energyKeys.map(key => (
          <KeyItem key={key}>
            <KeySquare background={energyTypeToColor[key]} />
            <Text textStyle="paragraph400">{getTypesCopy(key)}</Text>
          </KeyItem>
        ))}
      </KeyWrapper>
    </>
  );
};
